// 레이드 「잿불 비룡」 절차 효과음 레시피 표. 새 오디오 파일·외부 샘플 없음: synth/dsp.ts 렌더러가 이 표에서 만든다(c019와 같은 방식).
// 레이드 판에서만 은행을 채우고(raid/engine.ts), 레이드를 나가면 은행을 비운다(다른 판의 메모리를 쓰지 않는다).
// 음량 규칙: 렌더 결과는 묶음 목표 창 RMS(100ms 창 최대)에 맞추고 피크 -1.2dBFS 이하. 섞임 음량은 gainDb.
// 설계 메모는 줄마다 주석, 이벤트 표는 docs/AUDIO.md 「레이드 사운드」 절.
import { Filt, Noise, Wave, type Env, type FilterSpec, type Freq, type GrainLayer, type Layer, type NoiseLayer, type OscLayer, type Recipe, type Sweep } from '../synth/dsp';

/** 레이드 소리 묶음 */
export const RCat = { Boss: 0, Tele: 1, Strike: 2, Party: 3, Loop: 4, Jingle: 5, PartyHit: 6 } as const;

export interface RaidCatDef {
  readonly name: string;
  /** 렌더 목표 창 RMS(dBFS) */
  readonly targetDb: number;
  /** 동시 목소리 상한 */
  readonly cap: number;
  /** 같은 묶음 연속 시작 최소 간격(초)·미룰 수 있는 최대 시간(초, 0 = 버림) */
  readonly spacing: number;
  readonly maxDelay: number;
  /** 큰 타격 때 덕킹을 받는다(보스 포효·경고는 받지 않는다 — 반응해야 하는 소리) */
  readonly duck: boolean;
}

export const RAID_CATS: readonly RaidCatDef[] = [
  { name: 'boss', targetDb: -13, cap: 2, spacing: 0, maxDelay: 0, duck: false },
  { name: 'tele', targetDb: -14, cap: 2, spacing: 0, maxDelay: 0, duck: false },
  // 화염 장판 판정은 한 틱에 최대 6개가 터진다: 상한 3·40ms 간격(최대 120ms 미뤄 우르르 터지게)
  { name: 'strike', targetDb: -13, cap: 3, spacing: 0.04, maxDelay: 0.12, duck: false },
  // 직업 AI(지금 방패병·궁수·사제) 행동음: 작게, 큰 타격 때 덕킹
  { name: 'party', targetDb: -16, cap: 4, spacing: 0.03, maxDelay: 0.06, duck: true },
  // 반복음(불 장판 지글·날갯짓)은 풀을 쓰지 않고 전용 반복 채널에서 돈다
  { name: 'loop', targetDb: -16, cap: 0, spacing: 0, maxDelay: 0, duck: false },
  { name: 'jingle', targetDb: -14, cap: 1, spacing: 0, maxDelay: 0, duck: false },
  // 직업 AI 명중(발사음보다 늦게 예약): 발사음과 묶음을 나눠, 미래에 예약된 명중이 다음 발사음의 간격 계산을 밀어내지 않게 한다(Codex P2 #216)
  { name: 'partyHit', targetDb: -16, cap: 3, spacing: 0, maxDelay: 0, duck: true },
];

export interface RaidSoundDef {
  readonly name: string;
  readonly cat: number;
  /** 우선순위(높을수록 먼저, 모자라면 낮은 것부터 끊는다) */
  readonly prio: number;
  /** 섞임 음량(dB, 효과음 버스 앞) */
  readonly gainDb: number;
  /** 영웅 기준 좌우 팬·거리 감쇠 */
  readonly positional: boolean;
  /** 미리 렌더할 시드 변형 수(긴 소리 1, 자주 나는 짧은 소리 2) */
  readonly variants: 1 | 2;
  /** 반복음: 끝 loop초를 시작과 등전력 교차 페이드해 이음매 없이 돈다(재생 길이 = dur - loop) */
  readonly loop?: number;
  /** 재생 피치 흔들림을 끈다(징글) */
  readonly steady?: boolean;
  readonly recipe: Recipe;
}

// ── 짧은 작성 도우미(synth/recipes.ts와 같은 모양) ───────────────
const sw = (from: number, to: number, time: number): Sweep => ({ from, to, time });
const env = (a: number, d: number, h = 0, ac = 1): Env => ({ a, d, h, ac });
const lp = (freq: Freq, q = 0.7): FilterSpec => ({ type: Filt.Lp, freq, q });
const hp = (freq: Freq, q = 0.7): FilterSpec => ({ type: Filt.Hp, freq, q });
const bp = (freq: Freq, q = 1): FilterSpec => ({ type: Filt.Bp, freq, q });
type OscOpt = Partial<Pick<OscLayer, 'filter' | 'drive' | 'fm' | 'vib'>>;
const osc = (wave: number, freq: Freq, at: number, dur: number, e: Env, gainDb: number, o: OscOpt = {}): OscLayer => ({ kind: 'osc', wave, freq, at, dur, env: e, gainDb, ...o });
const sine = (freq: Freq, at: number, dur: number, e: Env, gainDb: number, o: OscOpt = {}): OscLayer => osc(Wave.Sine, freq, at, dur, e, gainDb, o);
const saw = (freq: Freq, at: number, dur: number, e: Env, gainDb: number, o: OscOpt = {}): OscLayer => osc(Wave.Saw, freq, at, dur, e, gainDb, o);
const noise = (color: number, at: number, dur: number, e: Env, gainDb: number, filter?: FilterSpec, drive?: number): NoiseLayer => ({ kind: 'noise', color, at, dur, env: e, gainDb, filter, drive });
type GrainOpt = Partial<Pick<GrainLayer, 'order' | 'fall' | 'wave' | 'fm' | 'filter'>>;
const grains = (count: number, lo: number, hi: number, at: number, spread: number, dur: number, e: Env, gainDb: number, o: GrainOpt = {}): GrainLayer => ({ kind: 'grains', count, lo, hi, at, spread, dur, env: e, gainDb, ...o });
const bell = (ratio: number, index: number, decay: number) => ({ ratio, index, decay });
/** 묵직한 저역 타격: 피치가 떨어지는 사인 + 포화 */
const thump = (from: number, to: number, at: number, dur: number, d: number, gainDb: number, drive = 2): OscLayer => sine(sw(from, to, Math.min(dur, 0.2)), at, dur, env(0.001, d), gainDb, { drive });
const click = (at: number, freq: number, q: number, gainDb: number, d = 0.006): NoiseLayer => noise(Noise.White, at, 0.04, env(0, d), gainDb, bp(freq, q));
const R = (dur: number, layers: Layer[], extra: Partial<Recipe> = {}): Recipe => ({ dur, layers, vary: { cents: 20, time: 0.003, db: 0.7 }, ...extra });

/**
 * 짐승 성대 한 줄: 톱니(성대) + 빠른 비브라토(거친 떨림) + 포화 + 대역 필터(입 모양, 스윕으로 열리고 닫힘).
 * 두 줄을 조금 다른 피치로 겹치면 큰 몸통의 두꺼운 포효가 된다.
 */
const growl = (freq: Freq, at: number, dur: number, e: Env, gainDb: number, mouth: Freq, q = 1.6, rough = 90, rate = 31): OscLayer =>
  saw(freq, at, dur, e, gainDb, { filter: bp(mouth, q), vib: { rate, cents: rough }, drive: 2.5 });
/** 아음(서브하모닉) 울림: 위상 FM 사인(1:0.5)이 성대 밑에 거친 저역을 깐다 */
const subRumble = (freq: Freq, at: number, dur: number, e: Env, gainDb: number): OscLayer =>
  sine(freq, at, dur, e, gainDb, { fm: bell(0.5, 2.2, dur), drive: 1.8 });
/** 숨(기류): 분홍 잡음 대역 */
const breath = (at: number, dur: number, e: Env, gainDb: number, f: Freq, q = 1.2): NoiseLayer => noise(Noise.Pink, at, dur, e, gainDb, bp(f, q));
/** 날개 한 번 내려치기: 공기 덩어리 '훅'(분홍 잡음 저역 — 갈색은 층마다 무작위 걸음이라 박자마다 음량이 5dB씩 흔들렸다) + 막이 펄럭이는 대역 잡음 + 아주 낮은 압력 */
const flap = (at: number, gainDb: number, heavy = 1): Layer[] => [
  noise(Noise.Pink, at, 0.32, env(0.035, 0.09), gainDb + 4, lp(260 * heavy, 1.1)),
  noise(Noise.Pink, at + 0.02, 0.26, env(0.04, 0.06), gainDb - 9, bp(sw(1100, 520, 0.2), 1.4)),
  sine(sw(55, 38, 0.2), at, 0.3, env(0.03, 0.08), gainDb - 4, { drive: 1.4 }),
];
/** 불꽃 튀는 딱딱 소리(짧은 삼각 핑 묶음) */
const crackle = (count: number, at: number, spread: number, gainDb: number, lo = 1200, hi = 5200): GrainLayer =>
  grains(count, lo, hi, at, spread, 0.012, env(0, 0.0025), gainDb, { wave: Wave.Tri });
/** 불길: 갈색 + 분홍 잡음(저역 통과·포화) */
const flame = (at: number, dur: number, e: Env, gainDb: number, cutoff: Freq = 900): Layer[] => [
  noise(Noise.Brown, at, dur, e, gainDb, lp(cutoff, 0.9), 1.6),
  noise(Noise.Pink, at, dur, e, gainDb - 7, bp(cutoff, 0.8), 1.2),
];
/** 화음 음표(징글) — 톱니를 저역 통과로 다듬은 금관 느낌 */
const brass = (f: number, at: number, dur: number, gainDb: number, open = 2400): OscLayer[] => [
  saw(f, at, dur, env(0.03, dur * 0.5, dur * 0.35), gainDb, { filter: lp(sw(open * 0.4, open, 0.12), 1.1), vib: { rate: 5.5, cents: 7 } }),
  saw(f * 1.004, at, dur, env(0.04, dur * 0.5, dur * 0.35), gainDb - 3, { filter: lp(open * 0.8, 0.9) }),
];

const C = RCat;
const DEFS = {
  // ── 비룡 몸 소리(보스 묶음) ─────────────────────────
  // 등장 포효(BossIntro, 등장 2.5초): 짧게 들이쉬고 → 위로 솟았다 길게 내려앉는 두 줄 성대 + 아음 + 거센 숨 + 입이 열렸다 닫히는 필터 + 끝 그르렁
  wyvRoar: { cat: C.Boss, prio: 9, gainDb: 0, positional: true, variants: 1, recipe: R(2.3, [
    breath(0, 0.28, env(0.2, 0.05, 0, 2), -14, sw(500, 1400, 0.25), 1.5),
    growl(sw(95, 150, 0.32), 0.18, 0.4, env(0.12, 0.2, 0.05), -6, sw(500, 1300, 0.3)),
    growl(sw(150, 82, 1.5), 0.42, 1.65, env(0.04, 0.55, 0.65), -4, sw(1400, 520, 1.5), 1.4, 110),
    growl(sw(158, 86, 1.5), 0.44, 1.6, env(0.06, 0.5, 0.6), -8, sw(2200, 800, 1.4), 2.2, 70, 23),
    subRumble(sw(62, 40, 1.6), 0.3, 1.9, env(0.15, 0.6, 0.6), -7),
    breath(0.4, 1.7, env(0.05, 0.6, 0.5), -12, sw(2600, 900, 1.5), 0.9),
    growl(sw(90, 70, 0.4), 1.75, 0.5, env(0.05, 0.18), -12, 420, 1.2, 160, 19),
  ], { delay: { time: 0.11, fb: 0.32, wet: 0.22, damp: 2500 }, drive: 1.25 }) },
  // 2페이즈 포효(BossPattern 9): 등장보다 높게 째지는 비명 + 두꺼운 밑 포효(분노가 끓어오름)
  wyvRoarPhase: { cat: C.Boss, prio: 9, gainDb: 0, positional: true, variants: 1, recipe: R(2.0, [
    growl(sw(210, 300, 0.25), 0, 0.5, env(0.03, 0.25, 0.1), -6, sw(1200, 2600, 0.25), 2.4, 60, 37),
    growl(sw(290, 160, 1.3), 0.25, 1.5, env(0.03, 0.45, 0.45), -6, sw(2600, 900, 1.3), 2, 80, 35),
    growl(sw(120, 75, 1.4), 0.1, 1.7, env(0.08, 0.55, 0.5), -6, sw(1000, 450, 1.4), 1.4, 120),
    subRumble(sw(58, 38, 1.5), 0.05, 1.8, env(0.1, 0.55, 0.5), -8),
    breath(0, 1.7, env(0.03, 0.55, 0.45), -13, sw(3500, 1200, 1.4), 0.8),
  ], { delay: { time: 0.12, fb: 0.35, wet: 0.24, damp: 2800 }, drive: 1.25 }) },
  // 광폭(BossPattern 10): 낮고 길게 끓는 포효 + 몸에서 불길이 확 일어나는 쉬익·딱딱
  wyvRoarEnrage: { cat: C.Boss, prio: 9, gainDb: 0, positional: true, variants: 1, recipe: R(2.2, [
    growl(sw(80, 115, 0.5), 0, 0.7, env(0.15, 0.3, 0.2), -6, sw(400, 1000, 0.5), 1.4, 130, 27),
    growl(sw(112, 62, 1.5), 0.55, 1.6, env(0.05, 0.6, 0.5), -5, sw(1100, 380, 1.5), 1.3, 140, 24),
    subRumble(sw(48, 32, 1.8), 0, 2.1, env(0.3, 0.7, 0.6), -6),
    ...flame(0.3, 1.8, env(0.5, 0.6, 0.3, 1.5), -9, sw(400, 1800, 1.0)),
    crackle(40, 0.4, 1.6, -15),
  ], { delay: { time: 0.1, fb: 0.3, wet: 0.2, damp: 2200 }, drive: 1.3 }) },
  // 이륙(BossPattern 4): 짧은 기합 포효 + 무거운 첫 날갯짓 셋(점점 빨라짐) + 바닥 흙먼지 쉬익
  wyvTakeoff: { cat: C.Boss, prio: 8, gainDb: -1, positional: true, variants: 1, recipe: R(1.5, [
    growl(sw(130, 95, 0.6), 0, 0.7, env(0.03, 0.25, 0.15), -8, sw(1300, 600, 0.6), 1.5, 100),
    ...flap(0.12, -4, 1.1), ...flap(0.62, -4, 1.05), ...flap(1.02, -5),
    noise(Noise.Pink, 0.15, 1.3, env(0.3, 0.5), -16, bp(sw(600, 2400, 1.0), 0.8)),
  ], { delay: { time: 0.08, fb: 0.25, wet: 0.16, damp: 2500 } }) },
  // 들이마심(BossPattern 5, 브레스 줄마다): 목구멍으로 빨려드는 쉬익(대역이 위로, 천천히 차오름) + 낮게 끓는 그르렁 + 끝 딸깍(입을 여는 순간)
  wyvInhale: { cat: C.Boss, prio: 8, gainDb: -2, positional: true, variants: 1, recipe: R(1.0, [
    breath(0, 0.95, env(0.8, 0.06, 0, 2.2), -6, sw(500, 2600, 0.9), 1.8),
    noise(Noise.White, 0, 0.95, env(0.85, 0.05, 0, 2.5), -18, hp(sw(2000, 6000, 0.9))),
    growl(sw(70, 92, 0.9), 0, 0.95, env(0.6, 0.1, 0, 1.6), -12, sw(300, 700, 0.9), 1.2, 150, 21),
    sine(sw(40, 52, 0.9), 0, 0.95, env(0.7, 0.08, 0, 1.6), -10, { drive: 1.5 }),
  ]) },
  // 화염 분사(브레스 불길이 나오는 순간, 렌더 불꽃과 같은 틱): 아주 빠른 시작의 거센 불길 쉬익 + 지속 포효 + 딱딱 + 깊은 저역 압력
  wyvBreath: { cat: C.Boss, prio: 9, gainDb: 0, positional: true, variants: 1, recipe: R(1.5, [
    noise(Noise.White, 0, 0.08, env(0.002, 0.03), -8, hp(1500)),
    ...flame(0, 1.45, env(0.02, 0.45, 0.6), -2, sw(2600, 900, 1.2)),
    noise(Noise.Pink, 0, 1.4, env(0.02, 0.4, 0.55), -12, hp(sw(3000, 1800, 1.0)), 1.2),
    growl(sw(105, 80, 1.2), 0.02, 1.3, env(0.05, 0.4, 0.5), -11, 700, 1.1, 120),
    sine(sw(60, 36, 0.5), 0, 1.3, env(0.01, 0.45, 0.4), -6, { drive: 2 }),
    crackle(55, 0.05, 1.3, -14),
  ], { drive: 1.35 }) },
  // 착지(착지 경고 판정): 땅이 꺼지는 초저역 쿵 + 터지는 흙 잡음 + 돌 부스러기 + 길게 구르는 땅울림
  wyvLand: { cat: C.Boss, prio: 9, gainDb: 0, positional: true, variants: 1, recipe: R(1.5, [
    noise(Noise.White, 0, 0.05, env(0, 0.012), -4, hp(1200)),
    thump(95, 30, 0, 0.9, 0.24, -1, 2.6),
    noise(Noise.Brown, 0, 0.6, env(0.002, 0.16), -4, lp(380), 1.5),
    noise(Noise.Pink, 0.005, 0.5, env(0.003, 0.12), -10, bp(sw(1800, 500, 0.4), 0.8)),
    grains(24, 500, 2600, 0.04, 0.7, 0.03, env(0, 0.007), -14, { wave: Wave.Tri, fall: 10 }),
    noise(Noise.Brown, 0.1, 1.35, env(0.08, 0.45), -9, lp(140)),
  ], { delay: { time: 0.09, fb: 0.3, wet: 0.2, damp: 1800 }, drive: 1.3 }) },
  // 쓰러짐(BossDefeated): 길게 꺾이며 내려앉는 마지막 포효 → 목 끓는 그르렁 → 몸이 땅에 닿는 쿵(1.9초) + 흙먼지
  wyvDeath: { cat: C.Boss, prio: 9, gainDb: 0, positional: true, variants: 1, recipe: R(3.0, [
    growl(sw(190, 120, 0.4), 0, 0.5, env(0.04, 0.2, 0.1), -6, sw(2200, 1200, 0.4), 2, 70, 35),
    growl(sw(125, 52, 1.6), 0.35, 1.6, env(0.05, 0.5, 0.5), -5, sw(1500, 380, 1.6), 1.4, 130, 25),
    subRumble(sw(55, 30, 1.8), 0.2, 1.9, env(0.1, 0.6, 0.5), -8),
    growl(sw(62, 44, 0.6), 1.45, 0.7, env(0.05, 0.25), -12, 300, 1.4, 220, 13),
    thump(80, 28, 1.9, 1.0, 0.25, -2, 2.4),
    noise(Noise.Brown, 1.9, 1.0, env(0.003, 0.25), -6, lp(300), 1.4),
    grains(16, 500, 2200, 1.93, 0.6, 0.03, env(0, 0.007), -16, { wave: Wave.Tri, fall: 10 }),
  ], { delay: { time: 0.12, fb: 0.32, wet: 0.22, damp: 2200 }, drive: 1.2 }) },
  // 경직(GuardBreak, 비룡): 아파서 짧게 째지는 비명 두 번 + 비틀거리는 쿵
  wyvStagger: { cat: C.Boss, prio: 7, gainDb: -2, positional: true, variants: 1, recipe: R(0.95, [
    growl(sw(320, 210, 0.3), 0, 0.35, env(0.01, 0.12), -5, sw(2400, 1300, 0.3), 2.4, 60, 39),
    growl(sw(260, 150, 0.4), 0.3, 0.5, env(0.02, 0.15), -7, sw(2000, 900, 0.4), 2.2, 80, 33),
    thump(120, 55, 0.45, 0.4, 0.1, -6),
    breath(0, 0.6, env(0.01, 0.2), -16, 2500, 0.8),
  ]) },

  // ── 비룡 패턴 경고(경고가 나타나는 틱, 첫 소리 10ms 안, 그 패턴 경고 시간보다 먼저 끝남) ─────
  // 물기(경고 1.0초): 위협하는 낮은 그르렁(입이 열리며 필터가 열림) + 이빨 사이 쉿
  teleWyvBite: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, variants: 1, recipe: R(0.9, [
    click(0, 900, 1.5, -10, 0.01),
    growl(sw(85, 125, 0.75), 0, 0.85, env(0.004, 0.25, 0.45), -4, sw(450, 1500, 0.75), 1.5, 130, 27),
    subRumble(sw(45, 60, 0.75), 0, 0.85, env(0.01, 0.3, 0.4), -9),
    noise(Noise.White, 0.35, 0.5, env(0.2, 0.08, 0.1), -15, bp(4200, 1.4)),
  ]) },
  // 꼬리(경고 1.1초): 비늘이 차르르 떨리는 소리(잦아지는 딱딱) + 꼬리를 뒤로 감는 바람(대역 상승) + 짧은 그르렁
  teleWyvTail: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, variants: 1, recipe: R(1.0, [
    noise(Noise.Pink, 0, 0.12, env(0.002, 0.04), -4, bp(900, 1.6)),
    grains(30, 1800, 4200, 0.02, 0.85, 0.02, env(0, 0.005), -19, { wave: Wave.Tri, order: 1, fall: -6 }),
    noise(Noise.Pink, 0, 0.95, env(0.75, 0.08, 0, 2), -4, bp(sw(400, 2200, 0.9), 1.8)),
    growl(sw(95, 80, 0.5), 0, 0.5, env(0.005, 0.2), -12, 600, 1.4, 110),
  ]) },
  // 화염 장판(경고 1.4초): 목에서 끓는 꾸르륵 → 불덩이를 토하는 '퉤' 셋 + 떨어지며 낮아지는 휘파람(불덩이가 원 위로 떨어짐)
  teleWyvPools: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, variants: 1, recipe: R(1.3, [
    growl(sw(75, 100, 0.3), 0, 0.4, env(0.004, 0.15, 0.1), -6, sw(300, 800, 0.3), 1.2, 200, 17),
    ...[0.32, 0.47, 0.62].flatMap((at, i): Layer[] => [
      noise(Noise.Brown, at, 0.18, env(0.003, 0.05), -4 - i, lp(500), 1.5),
      noise(Noise.Pink, at, 0.15, env(0.002, 0.04), -10 - i, bp(1400, 1.2)),
      sine(sw(1500 - i * 120, 600 - i * 50, 0.55), at + 0.08, 0.55, env(0.05, 0.25, 0.15), -15),
    ]),
    crackle(18, 0.35, 0.8, -16),
  ]) },
  // 돌진(경고 1.2초): 콧김 둘 + 앞발로 땅 긁기 둘 + 차오르는 낮은 포효(돌진 직전 최고조)
  teleWyvCharge: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, variants: 1, recipe: R(1.1, [
    breath(0, 0.16, env(0.003, 0.05), -4, 800, 1.6),
    breath(0.18, 0.16, env(0.003, 0.05), -6, 700, 1.6),
    noise(Noise.White, 0.36, 0.22, env(0.01, 0.06), -10, bp(sw(1600, 700, 0.2), 2)),
    noise(Noise.White, 0.6, 0.22, env(0.01, 0.06), -9, bp(sw(1600, 700, 0.2), 2)),
    growl(sw(70, 120, 1.0), 0.05, 1.0, env(0.8, 0.08, 0, 1.8), -5, sw(350, 1300, 1.0), 1.4, 120, 25),
    sine(sw(36, 50, 1.0), 0, 1.05, env(0.8, 0.06, 0, 1.6), -8, { drive: 1.6 }),
  ]) },
  // 착지 경고(1.4초): 하늘에서 내리꽂히는 바람(대역이 높은 데서 낮게) + 커지는 날개 펄럭 + 낮게 차오르는 압력
  teleWyvLand: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, variants: 1, recipe: R(1.3, [
    noise(Noise.White, 0, 0.06, env(0.001, 0.02), -10, bp(3000, 1.2)),
    noise(Noise.Pink, 0, 1.25, env(0.9, 0.08, 0, 1.8), -6, bp(sw(3200, 450, 1.2), 1.5)),
    ...flap(0.15, -9), ...flap(0.6, -8), ...flap(0.98, -7),
    sine(sw(30, 46, 1.2), 0, 1.25, env(0.9, 0.06, 0, 1.8), -7, { drive: 1.6 }),
  ]) },

  // ── 비룡 공격 판정(TelegraphFire, 판정 순간) ─────────────────
  // 물기 판정: 이빨이 맞물리는 딱 둘 + 묵직한 턱 쿵 + 짧은 으르렁
  wyvBite: { cat: C.Strike, prio: 8, gainDb: 0, positional: true, variants: 2, recipe: R(0.55, [
    click(0, 1700, 1.8, -2, 0.01),
    click(0.03, 1300, 1.8, -5, 0.01),
    thump(160, 60, 0, 0.3, 0.07, -3, 2.2),
    noise(Noise.Pink, 0, 0.2, env(0.002, 0.05), -9, bp(700, 1)),
    growl(sw(110, 80, 0.35), 0.04, 0.4, env(0.01, 0.12), -10, 800, 1.3, 140),
  ]) },
  // 꼬리 판정: 공기를 가르는 큰 휘익(대역 하강) + 채찍 같은 딱 + 둔한 쿵
  wyvTail: { cat: C.Strike, prio: 8, gainDb: 0, positional: true, variants: 2, recipe: R(0.65, [
    noise(Noise.White, 0, 0.35, env(0.04, 0.08), -4, bp(sw(2600, 500, 0.3), 1.3)),
    click(0.09, 3200, 1.2, -4, 0.006),
    thump(130, 50, 0.1, 0.35, 0.09, -3, 2.2),
    noise(Noise.Brown, 0.1, 0.4, env(0.003, 0.1), -8, lp(300)),
  ]) },
  // 화염 장판 판정(불덩이 착탄): 펑 저역 + 불길이 확 퍼지는 쉬익 + 딱딱 불티 — 장판이 남는다
  wyvFireBurst: { cat: C.Strike, prio: 7, gainDb: -2, positional: true, variants: 2, recipe: R(0.85, [
    thump(120, 45, 0, 0.4, 0.1, -3, 2.2),
    noise(Noise.White, 0, 0.06, env(0.001, 0.015), -8, hp(1500)),
    ...flame(0, 0.8, env(0.008, 0.22), -4, sw(2400, 700, 0.6)),
    crackle(22, 0.04, 0.6, -14),
  ]) },
  // 돌진 판정: 땅을 박차는 쿵쿵쿵쿵(점점 빨라짐) + 발톱 긁힘 + 바람 + 짧은 기합
  wyvCharge: { cat: C.Strike, prio: 8, gainDb: 0, positional: true, variants: 1, recipe: R(1.0, [
    ...[0, 0.13, 0.24, 0.33].map((at, i) => thump(110 - i * 5, 45, at, 0.25, 0.07, -3 - i * 0.5, 2.2)),
    ...[0, 0.13, 0.24, 0.33].map((at) => noise(Noise.Pink, at, 0.12, env(0.002, 0.03), -12, bp(1100, 1.2))),
    noise(Noise.Pink, 0, 0.6, env(0.05, 0.2), -10, bp(sw(1500, 600, 0.5), 1)),
    growl(sw(120, 90, 0.4), 0, 0.45, env(0.01, 0.15), -11, 900, 1.4, 120),
    noise(Noise.Brown, 0.3, 0.65, env(0.05, 0.2), -10, lp(150)),
  ]) },

  // ── 반복음(전용 채널) ─────────────────────────────
  // 불 장판 지글(장판마다, 거리 감쇠): 낮게 타오르는 불길 + 흩어진 딱딱 불티 + 쉿. 2.0초 반복(끝 0.3초 교차 페이드)
  fireLoop: { cat: C.Loop, prio: 1, gainDb: -10, positional: true, variants: 1, loop: 0.3, recipe: R(2.3, [
    noise(Noise.Brown, 0, 2.3, env(0.01, 9, 2.2), -6, lp(700, 0.8), 1.3),
    noise(Noise.Pink, 0, 2.3, env(0.01, 9, 2.2), -16, hp(3200)),
    crackle(46, 0, 2.3, -9),
    grains(14, 300, 900, 0, 2.3, 0.04, env(0.002, 0.012), -14, { wave: Wave.Sine }),
  ]) },
  // 날갯짓(공중에 있는 동안): 0.52초마다 한 번(비룡 렌더 날갯짓 주기와 같은 박자) — 셋이 한 바퀴, 1.56초 반복(끝 0.2초 교차 페이드)
  wingLoop: { cat: C.Loop, prio: 1, gainDb: -2, positional: true, variants: 1, loop: 0.2, recipe: R(1.76, [
    ...flap(0, -2), ...flap(0.52, -2.5), ...flap(1.04, -2), ...flap(1.56, -2),
    noise(Noise.Pink, 0, 1.76, env(0.01, 9, 1.7), -24, bp(700, 0.7)),
  ]) },

  // ── 직업 AI(파티) 행동음 ─────────────────────────────
  // 방패병 공격: 묵직한 검 휘두름(대역 하강) + 짧은 쇠 끝
  mercSwing: { cat: C.Party, prio: 3, gainDb: -6, positional: true, variants: 2, recipe: R(0.32, [
    noise(Noise.White, 0, 0.26, env(0.03, 0.06), -6, bp(sw(2200, 700, 0.22), 1.4)),
    sine(2600, 0.04, 0.2, env(0.001, 0.04), -16, { fm: bell(1.414, 2, 0.03) }),
  ]) },
  // 궁수 공격: 시위 튕김(삼각 '퉁') + 딸깍 + 화살이 날아가는 쉭(라이라보다 가볍고 높다)
  mercBow: { cat: C.Party, prio: 3, gainDb: -7, positional: true, variants: 2, recipe: R(0.3, [
    click(0, 3600, 2, -5, 0.005),
    osc(Wave.Tri, sw(330, 240, 0.06), 0, 0.22, env(0.001, 0.05), -6, { filter: lp(2600) }),
    noise(Noise.White, 0.01, 0.22, env(0.004, 0.06), -10, bp(sw(5200, 2200, 0.18), 1.6)),
  ]) },
  // 궁수 화살 명중(보스 몸): 둔한 '턱' + 나무 떨림
  mercArrowHit: { cat: C.PartyHit, prio: 3, gainDb: -8, positional: true, variants: 2, recipe: R(0.22, [
    thump(300, 130, 0, 0.12, 0.03, -4, 1.8),
    click(0, 2000, 2, -6, 0.006),
    osc(Wave.Tri, 180, 0.01, 0.15, env(0.001, 0.04), -14, { vib: { rate: 40, cents: 60 } }),
  ]) },
  // 사제 공격: 빛 화살 — 위로 솟는 맑은 음 + 반짝이 + 짧은 쉭
  mercHolyBolt: { cat: C.Party, prio: 3, gainDb: -9, positional: true, variants: 2, recipe: R(0.45, [
    sine(sw(700, 1800, 0.12), 0, 0.3, env(0.002, 0.08), -6, { fm: bell(2, 1, 0.05) }),
    grains(6, 2500, 6000, 0.02, 0.2, 0.06, env(0.001, 0.02), -16, { order: 1 }),
    noise(Noise.Pink, 0, 0.25, env(0.01, 0.06), -14, hp(3500)),
  ]) },
  // 사제 빛 화살 명중: 부드러운 빛 터짐(벨 + 고역 쉬익)
  mercHolyHit: { cat: C.PartyHit, prio: 3, gainDb: -10, positional: true, variants: 2, recipe: R(0.35, [
    sine(1568, 0, 0.3, env(0.001, 0.07), -6, { fm: bell(3.5, 1.4, 0.03) }),
    noise(Noise.White, 0, 0.12, env(0.001, 0.03), -12, hp(4000)),
    thump(220, 120, 0, 0.1, 0.03, -14, 1.2),
  ]) },
  // 사제 치유: 맑게 오르는 벨 세 음(G5 B5 D6) + 따뜻한 패드 + 위로 흩어지는 반짝이 — 치유 종
  mercHeal: { cat: C.Party, prio: 5, gainDb: -6, positional: true, variants: 1, recipe: R(1.1, [
    ...[784, 987.77, 1174.66].map((f, i) => sine(f, i * 0.07, 0.9, env(0.002, 0.28), -7 - i, { fm: bell(2, 1.1, 0.12) })),
    sine(2349.3, 0.2, 0.7, env(0.002, 0.2), -16, { fm: bell(3.5, 1, 0.05) }),
    ...[392, 493.88].map((f) => osc(Wave.Tri, f, 0.03, 1.0, env(0.15, 0.35), -18, { vib: { rate: 5, cents: 8 } })),
    grains(12, 3000, 8000, 0.1, 0.7, 0.08, env(0.001, 0.03), -18, { order: 1, fall: 6 }),
  ], { delay: { time: 0.1, fb: 0.35, wet: 0.25, damp: 6000 } }) },
  // 방패병 도발: 굵은 외침 '하앗!'(톱니 성대 + 모음 포먼트 두 대역, 피치가 꺾여 내려감) + 방패를 내리찍는 쇠 쿵
  mercTaunt: { cat: C.Party, prio: 5, gainDb: -4, positional: true, variants: 1, recipe: R(0.9, [
    saw(sw(175, 128, 0.4), 0, 0.42, env(0.02, 0.16, 0.12), -4, { filter: bp(730, 3.5), vib: { rate: 6, cents: 25 }, drive: 1.6 }),
    saw(sw(175, 128, 0.4), 0, 0.42, env(0.02, 0.16, 0.12), -8, { filter: bp(1150, 4.5), vib: { rate: 6, cents: 25 } }),
    noise(Noise.Pink, 0, 0.3, env(0.01, 0.1), -16, bp(2500, 1.5)),
    noise(Noise.White, 0.32, 0.04, env(0, 0.008), -4, hp(2000)),
    sine(820, 0.32, 0.55, env(0.0005, 0.16), -6, { fm: bell(1.414, 4.5, 0.06) }),
    thump(140, 55, 0.32, 0.4, 0.1, -3, 2.2),
  ], { delay: { time: 0.07, fb: 0.25, wet: 0.16, damp: 4000 } }) },
  // 쓰러짐: 몸이 땅에 닿는 둔한 쿵 + 갑옷 짤그랑 + 아래로 내려앉는 낮은 음
  mercDown: { cat: C.Party, prio: 5, gainDb: -6, positional: true, variants: 1, recipe: R(0.75, [
    thump(150, 60, 0, 0.3, 0.08, -3, 1.8),
    grains(6, 1800, 3600, 0.02, 0.15, 0.06, env(0.001, 0.02), -12, { fm: bell(1.414, 1.5, 0.02) }),
    osc(Wave.Tri, sw(330, 165, 0.5), 0.08, 0.6, env(0.02, 0.2), -12, { filter: lp(1400) }),
  ]) },
  // 다시 일어남: 부드럽게 오르는 두 음 차임 + 반짝이
  mercRevive: { cat: C.Party, prio: 4, gainDb: -8, positional: true, variants: 1, recipe: R(0.85, [
    sine(sw(440, 660, 0.25), 0, 0.5, env(0.05, 0.15), -10),
    ...[659.25, 987.77].map((f, i) => sine(f, 0.12 + i * 0.1, 0.6, env(0.002, 0.18), -8, { fm: bell(2, 0.9, 0.08) })),
    grains(8, 2500, 6000, 0.15, 0.5, 0.08, env(0.001, 0.025), -18, { order: 1 }),
  ]) },
  // 피하기: 가벼운 구르기 쉭
  mercDodge: { cat: C.Party, prio: 1, gainDb: -14, positional: true, variants: 2, recipe: R(0.25, [
    noise(Noise.Pink, 0, 0.22, env(0.03, 0.05), -6, bp(sw(1800, 700, 0.2), 1.2)),
  ]) },

  // ── 징글(화면 소리) ──────────────────────────────
  // 레이드 승리: 팀파니 쿵 둘 → 금관 화음 D–F#–A 상승 → 높은 D 장화음이 길게 울리며 반짝이(3.4초)
  raidVictory: { cat: C.Jingle, prio: 9, gainDb: -2, positional: false, variants: 1, steady: true, recipe: R(3.4, [
    thump(110, 70, 0, 0.5, 0.18, -4, 1.6),
    thump(110, 70, 0.22, 0.5, 0.18, -4, 1.6),
    ...brass(293.66, 0.22, 0.32, -10), ...brass(369.99, 0.42, 0.32, -10), ...brass(440, 0.62, 0.36, -9),
    ...[587.33, 739.99, 880, 293.66].flatMap((f) => brass(f, 0.98, 2.3, -11, 3200)),
    thump(90, 50, 0.98, 0.9, 0.3, -3, 1.8),
    noise(Noise.White, 0.98, 1.6, env(0.002, 0.5), -24, hp(6000)),
    grains(26, 2500, 9000, 1.0, 1.8, 0.12, env(0.001, 0.04), -18, { order: 1, fall: 10, fm: bell(2.76, 1, 0.03) }),
  ], { delay: { time: 0.16, fb: 0.4, wet: 0.28, damp: 5000 } }) },
  // 레이드 전멸: 낮은 북 하나 → 내려앉는 단조 금관(D–C–A♭… 반음 하강) → 어두운 D 단화음이 사라짐(3.0초)
  raidWipe: { cat: C.Jingle, prio: 9, gainDb: -3, positional: false, variants: 1, steady: true, recipe: R(3.0, [
    thump(80, 40, 0, 0.9, 0.3, -3, 1.6),
    ...brass(293.66, 0.15, 0.45, -11, 1600), ...brass(261.63, 0.55, 0.45, -11, 1500), ...brass(207.65, 0.95, 0.5, -11, 1400),
    ...[146.83, 174.61, 220].flatMap((f) => brass(f, 1.4, 1.55, -12, 1100)),
    noise(Noise.Brown, 1.4, 1.5, env(0.3, 0.6), -14, lp(200)),
  ], { delay: { time: 0.18, fb: 0.35, wet: 0.22, damp: 2500 } }) },

  // ── 하드 잿불 낙인(C13) — 절차 합성(FlowMusic 실녹음 전 레시피, 원 소리 없음) ─────────
  // 낙인 시작(BossPattern brand, c ≥ 0): 땅 밑에서 차오르는 낮은 울림 + 표식이 '찰칵' 박히는 금속성 두 번(FM 종) + 달군 쇠 지글
  wyvBrandMark: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, variants: 1, recipe: R(1.5, [
    click(0, 2600, 1.4, -6, 0.008),
    sine(1318.5, 0, 1.0, env(0.001, 0.32), -9, { fm: bell(1.41, 2.4, 0.35) }),
    sine(987.77, 0.13, 1.1, env(0.001, 0.4), -10, { fm: bell(2.76, 1.6, 0.4) }),
    subRumble(sw(38, 56, 1.0), 0, 1.45, env(0.35, 0.5, 0.35), -6),
    growl(sw(70, 58, 1.2), 0.08, 1.2, env(0.3, 0.45, 0.2), -14, 420, 1.2, 140, 19),
    noise(Noise.White, 0.05, 1.2, env(0.15, 0.6), -24, hp(5200)),
    crackle(10, 0.2, 0.9, -20, 1800, 6000),
  ]) },
  // 카운트다운 틱(남은 시간 0.5초마다, 마지막 1.5초는 0.2초마다 — decide.brandTickAt): 짧은 쇠 '틱' + 작은 불티
  wyvBrandTick: { cat: C.Tele, prio: 6, gainDb: -5, positional: true, variants: 1, recipe: R(0.16, [
    click(0, 3400, 2, -3, 0.005),
    sine(1760, 0, 0.14, env(0.001, 0.045), -8, { fm: bell(1.41, 1.2, 0.05) }),
    thump(220, 110, 0, 0.08, 0.025, -12, 1.4),
  ]) },
  // 낙인 폭발(BossPattern brand, c = -1): 묵직한 펑 + 위로 치솟는 불기둥 쉬익(대역 상승) + 쇠가 터지는 날카로운 딱 + 불티 + 낮은 꼬리
  wyvBrandBlast: { cat: C.Strike, prio: 8, gainDb: 0, positional: true, variants: 2, recipe: R(1.2, [
    noise(Noise.White, 0, 0.05, env(0, 0.012), -5, hp(1800)),
    thump(150, 38, 0, 0.6, 0.16, -1, 2.6),
    ...flame(0, 0.9, env(0.004, 0.3), -3, sw(600, 2600, 0.35)),
    sine(sw(880, 440, 0.4), 0.01, 0.5, env(0.001, 0.12), -13, { fm: bell(2.76, 2, 0.1) }),
    crackle(26, 0.03, 0.8, -14),
    noise(Noise.Brown, 0.15, 1.0, env(0.06, 0.4), -10, lp(160)),
  ], { delay: { time: 0.08, fb: 0.25, wet: 0.16, damp: 2200 } }) },
} satisfies Record<string, Omit<RaidSoundDef, 'name'>>;

export type RaidSoundName = keyof typeof DEFS;
export const RAID_NAMES = Object.keys(DEFS) as RaidSoundName[];
/** 이름 → 번호(재생 경로는 번호만 쓴다) */
export const RY = Object.fromEntries(RAID_NAMES.map((n, i) => [n, i])) as { readonly [K in RaidSoundName]: number };
export const RAID_DEFS: readonly RaidSoundDef[] = RAID_NAMES.map((name) => ({ name, ...DEFS[name] }));
/** 렌더 샘플레이트(Hz) — c019 은행과 같다 */
export const RAID_SR = 32000;
/** 변형 시드: 소리 번호·변형 번호로 고정(c019 시드와 겹치지 않게 다른 상수) */
export function raidSeed(id: number, v: number): number {
  return (0x7a1d5e3b ^ Math.imul(id + 1, 0x9e3779b1) ^ Math.imul(v + 1, 0xc2b2ae35)) >>> 0;
}

/**
 * 반복음 이음매: 렌더한 x(길이 L + loop)의 끝 loop초를 시작 loop초와 등전력(cos/sin) 교차 페이드해 길이 L의 고리로 만든다.
 * 마지막 샘플 다음이 첫 샘플로 자연스럽게 이어진다(첫 loop초 = 꼬리가 사라지며 머리가 들어온다). 렌더 경로(초기화)에서만 부른다.
 */
export function makeLoop(x: Float32Array, sr: number, loop: number): Float32Array {
  const X = Math.max(1, Math.round(loop * sr)), L = x.length - X;
  const out = new Float32Array(L);
  out.set(x.subarray(0, L));
  for (let i = 0; i < X; i++) {
    const u = (i + 0.5) / X, a = Math.sin(u * Math.PI * 0.5), b = Math.cos(u * Math.PI * 0.5);
    out[i] = x[i] * a + x[L + i] * b;
  }
  return out;
}
